import React, { useMemo } from 'react';

import { Box, Flex, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { Undo2Icon } from 'lucide-react';

import { Markdown } from '@/components/common';
import { Button, Tag } from '@/components/core';
import { FONTS_OPACITY } from '@/constant';
import { cn } from '@/lib/utils';
import { useAgents } from '@/store/agents';
import { Session } from '@/types/chat';

import { MARKDOWN_COMPONENTS } from './MarkdownComponents';

interface CustomLangflowChatProps {
  className?: string;
  sessions: Session[];
  onSessionChangeClick?: (session: Session) => void;
  onReturnToChatClick?: () => void;
}

const SessionList: React.FC<CustomLangflowChatProps> = ({
  className = '',
  sessions,
  onSessionChangeClick,
  onReturnToChatClick,
}) => {
  const { data: agents } = useAgents();
  const agentAvatarByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents ?? []) {
      if (agent.avatar?.url) {
        map.set(agent.name, agent.avatar.url);
      }
    }
    return map;
  }, [agents]);

  const handleSessionChange = (session: Session) => () => {
    setTimeout(() => {
      onSessionChangeClick?.(session);
    }, 0);
  };

  return (
    <Stack
      gap={0}
      position='absolute'
      top='0'
      left='0'
      right='0'
      bottom='0'
      bg='cards.bg'
      overflowY='auto'
      className={cn(className)}
    >
      <HStack
        justifyContent='space-between'
        p={4}
        borderBottom='1px solid'
        borderColor='border.light'
      >
        <Text size='primarySemiBold'>Sessions</Text>
        <Button
          size='sm'
          variant='link'
          color='blue.500'
          onClick={onReturnToChatClick}
          rightIcon={<Icon as={Undo2Icon} boxSize={4} />}
        >
          Return to chat
        </Button>
      </HStack>
      {sessions.map(session => {
        const avatarUrl =
          session._agent.avatar.url ||
          agentAvatarByName.get(session._agent.name) ||
          '';
        const agentLabel = session._agent.name || 'AI assistant';

        return (
        <Box
          key={session.id}
          cursor='pointer'
          transition='all 0.3s ease-out'
          _hover={{
            bg: 'cards.bg-hover',
          }}
          onClick={handleSessionChange(session)}
        >
          <HStack
            p={4}
            borderBottom='1px solid'
            borderColor='border.light'
            gap={3}
          >
            <Stack flex={1}>
              <HStack>
                <Box
                  flexShrink={0}
                  boxSize={8}
                  rounded='full'
                  overflow='hidden'
                  bg='white.100'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={agentLabel} />
                  ) : (
                    <Text fontSize='xs' fontWeight='semibold'>
                      {session._agent.name?.charAt(0) || 'AI'}
                    </Text>
                  )}
                </Box>
                <Text size='secondarySemiBold' color='accent.500' noOfLines={2}>
                  {session.title}
                </Text>
              </HStack>
              {session.description ? (
                <Text
                  size='notePrimaryRegular'
                  opacity={FONTS_OPACITY}
                  noOfLines={2}
                >
                  {session.description}
                </Text>
              ) : null}
              {session.last_message ? (
                <Flex gap={2} opacity={FONTS_OPACITY}>
                  <Text size='notePrimarySemiBold' _empty={{ display: 'none' }}>
                    {session.last_message_sender_type === 'agent'
                      ? session._agent.name + ':'
                      : 'You:'}
                  </Text>
                  {session.last_message_sender_type === 'agent' ? (
                    <Text noOfLines={5}>
                      <Markdown components={MARKDOWN_COMPONENTS}>
                        {session.last_message}
                      </Markdown>
                    </Text>
                  ) : (
                    <Text
                      size='notePrimaryRegular'
                      fontStyle='italic'
                      wordBreak='break-all'
                      noOfLines={2}
                    >
                      {session.last_message}
                    </Text>
                  )}
                </Flex>
              ) : null}
              <Text size='notePrimaryRegular' opacity={FONTS_OPACITY}>
                {new Date(
                  session.last_message_timestamp ||
                    session.updated_at ||
                    session.created_at
                ).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  weekday: 'long',
                })}
                {', '}
                {new Date(
                  session.last_message_timestamp ||
                    session.updated_at ||
                    session.created_at
                ).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
                {typeof session.turnCount === 'number' ? (
                  <>
                    {' · '}
                    {session.turnCount === 1
                      ? '1 turn'
                      : `${session.turnCount} turns`}
                  </>
                ) : null}
              </Text>
            </Stack>
            <Tag
              bg='alternative.green-20'
              color='alternative.green'
              textTransform='capitalize'
              isReadOnly
            >
              {session.status}
            </Tag>
          </HStack>
        </Box>
        );
      })}
    </Stack>
  );
};

export { SessionList };
